import { useRef, useState } from "react";
import { Download, FileText, Loader2, Printer, Receipt, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const waText = (text) => `https://wa.me/?text=${encodeURIComponent(text)}`;

export default function ShipmentDocs({ shipment }) {
  const [busy, setBusy] = useState("");
  const [markup, setMarkup] = useState("");
  const [showReceipt, setShowReceipt] = useState(false);
  const pdfCache = useRef({});
  const labelCache = useRef(null);

  const fetchPdf = async (kind, qs = "") => {
    const res = await api.get(`/shipments/${shipment.id}/${kind}${qs}`, { responseType: "blob" });
    return new File([res.data], `INDIAFAST-${kind.replace(".pdf", "")}-${shipment.awb}.pdf`, { type: "application/pdf" });
  };

  // Prefetch on pointer-down so navigator.share() fires inside the browser's user-gesture window
  const prefetchPdf = (kind, qs = "") => {
    const key = `${kind}${qs}`;
    if (!pdfCache.current[key]) {
      pdfCache.current[key] = fetchPdf(kind, qs).catch((e) => { delete pdfCache.current[key]; throw e; });
    }
    return pdfCache.current[key];
  };

  const prefetchLabel = () => {
    if (!labelCache.current) {
      labelCache.current = api.get(`/shipments/${shipment.id}/label`).catch((e) => { labelCache.current = null; throw e; });
    }
    return labelCache.current;
  };

  const saveFile = (file) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(file);
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  const waFallback = (kind) => {
    const ph = String(shipment.delivery?.phone || shipment.pickup?.phone || "").replace(/\D/g, "").slice(-4);
    const link = `${window.location.origin}/api/public/bill/${shipment.awb}.pdf?phone=${ph}`;
    window.open(waText(`INDIAFAST document (${kind.replace(".pdf", "")}) for AWB ${shipment.awb}: ${link}`), "_blank");
  };

  const downloadPdf = async (kind, qs = "") => {
    setBusy(kind);
    try {
      saveFile(await fetchPdf(kind, qs));
      toast.success("PDF downloaded");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); }
  };

  const sharePdf = async (kind, qs = "") => {
    setBusy(`share-${kind}`);
    try {
      const file = await prefetchPdf(kind, qs);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: file.name });
        return;
      }
      waFallback(kind);
    } catch (err) {
      if (err?.name === "AbortError") { setBusy(""); return; }
      if (err?.name === "NotAllowedError") {
        try { saveFile(await prefetchPdf(kind, qs)); } catch { /* ignore */ }
        waFallback(kind);
        toast.info("Direct share was blocked — PDF downloaded and WhatsApp link opened instead.");
        setBusy("");
        return;
      }
      toast.error(apiError(err));
    } finally { setBusy(""); }
  };

  const labelToFile = async (label) => {
    if (label.startsWith("http")) {
      const blob = await (await fetch(label)).blob();
      return new File([blob], `INDIAFAST-label-${shipment.awb}.pdf`, { type: blob.type || "application/pdf" });
    }
    const isPng = label.startsWith("data:image/");
    const b64 = label.replace(/^data:.*?;base64,/, "");
    const bytes = Uint8Array.from(atob(b64), (ch) => ch.charCodeAt(0));
    return new File([bytes], `INDIAFAST-label-${shipment.awb}.${isPng ? "png" : "pdf"}`,
      { type: isPng ? "image/png" : "application/pdf" });
  };

  const downloadLabel = async () => {
    setBusy("label");
    try {
      const res = await prefetchLabel();
      const label = res.data.label;
      if (!label) { toast.info(res.data.message || "Label not ready yet"); return; }
      if (label.startsWith("http")) { window.open(label, "_blank"); return; }
      saveFile(await labelToFile(label));
      toast.success("Label downloaded");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); }
  };

  const shareLabel = async () => {
    setBusy("share-label");
    try {
      const res = await prefetchLabel();
      const label = res.data.label;
      if (!label) { toast.info(res.data.message || "Label not ready yet"); return; }
      const file = await labelToFile(label);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: file.name });
        return;
      }
      saveFile(file);
      toast.info("Sharing files not supported here — label downloaded instead");
    } catch (err) {
      if (err?.name === "AbortError") { setBusy(""); return; }
      if (err?.name === "NotAllowedError") {
        try { saveFile(await labelToFile((await prefetchLabel()).data.label)); } catch { /* ignore */ }
        toast.info("Direct share was blocked — label downloaded instead.");
        setBusy("");
        return;
      }
      toast.error(apiError(err));
    } finally { setBusy(""); }
  };

  const btn = "flex items-center gap-1.5 rounded-full border border-slate-300 px-3.5 py-2 text-xs font-bold text-navy transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60";
  const shareBtn = "flex items-center gap-1.5 rounded-full border border-emerald-500/50 bg-emerald-50 px-3.5 py-2 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-60";

  const rows = [
    { kind: "invoice.pdf", label: "Invoice PDF (A4)", icon: FileText, testid: "invoice" },
    { kind: "labels.pdf", label: `Box Labels PDF (A4) (${(shipment.boxes || shipment.package?.boxes || []).length || 1})`, icon: Printer, testid: "labels" },
  ];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="shipment-docs">
      <h3 className="mb-4 font-heading text-lg font-bold text-navy">Documents</h3>
      <div className="space-y-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-36 text-xs font-bold text-slate-500">Shipping Label</span>
          <button onClick={downloadLabel} disabled={!!busy} data-testid="download-label-btn" className={btn}>
            {busy === "label" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download
          </button>
          <button onClick={shareLabel} onPointerDown={prefetchLabel} disabled={!!busy} data-testid="share-label-btn" className={shareBtn}>
            {busy === "share-label" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />} WhatsApp
          </button>
        </div>
        {rows.map((r) => (
          <div key={r.kind} className="flex flex-wrap items-center gap-2">
            <span className="w-36 text-xs font-bold text-slate-500">{r.label}</span>
            <button onClick={() => downloadPdf(r.kind)} disabled={!!busy} data-testid={`download-${r.testid}-btn`} className={btn}>
              {busy === r.kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download
            </button>
            <button onClick={() => sharePdf(r.kind)} onPointerDown={() => prefetchPdf(r.kind)} disabled={!!busy} data-testid={`share-${r.testid}-btn`} className={shareBtn}>
              {busy === `share-${r.kind}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />} WhatsApp
            </button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-36 text-xs font-bold text-slate-500">Customer Receipt (A4)</span>
          <button onClick={() => setShowReceipt(true)} disabled={!!busy} data-testid="print-receipt-btn" className={btn}>
            <Receipt className="h-3.5 w-3.5" /> Markup & Get PDF
          </button>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-slate-400">Real A4 PDF files — Download saves to the device, WhatsApp shares the PDF directly (or a download link where file sharing isn't supported).</p>

      {showReceipt && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="receipt-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6">
            <h3 className="font-heading text-lg font-bold text-navy">Customer Receipt PDF</h3>
            <p className="mt-1 text-xs text-slate-500">Add your service/markup amount — it appears only on the receipt PDF, never in our records.</p>
            <div className="mt-4">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Your Markup (₹)</label>
              <input type="number" min="0" step="1" data-testid="receipt-markup-input" value={markup}
                     onChange={(e) => setMarkup(e.target.value)} placeholder="e.g. 50"
                     className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-saffron" />
              <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
                <div className="flex justify-between"><span>Shipping</span><span>₹{shipment.customer_price}</span></div>
                <div className="flex justify-between"><span>Your markup</span><span>₹{parseFloat(markup) || 0}</span></div>
                <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-bold">
                  <span>Customer pays</span><span data-testid="receipt-total">₹{(shipment.customer_price + (parseFloat(markup) || 0)).toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button onClick={() => setShowReceipt(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-600">Cancel</button>
              <button onClick={async () => { setShowReceipt(false); await downloadPdf("receipt.pdf", `?markup=${parseFloat(markup) || 0}`); }}
                      data-testid="receipt-download-btn"
                      className="flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-bold text-white hover:bg-slate-700">
                <Download className="h-4 w-4" /> Download
              </button>
              <button onClick={async () => { setShowReceipt(false); await sharePdf("receipt.pdf", `?markup=${parseFloat(markup) || 0}`); }}
                      onPointerDown={() => prefetchPdf("receipt.pdf", `?markup=${parseFloat(markup) || 0}`)}
                      data-testid="receipt-share-btn"
                      className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700">
                <Share2 className="h-4 w-4" /> WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
